import { useState } from 'react';

import { type Snippet, SnippetScope } from '../snippetTypes.ts';
import { DEFAULT_CATEGORY_ID, SNIPPET_CATEGORY_IDS } from '../constants/snippetCategories.ts';
import { DEFAULT_SNIPPET_SORT_ORDER, SnippetSortOrder } from '../constants/snippetSortOrder.ts';

/**
 * Pure function to filter snippets based on category and search query.
 * Extracted for testability and reuse.
 */
export const filterSnippets = (
	snippets: Snippet[],
	categoryId: string,
	searchQuery: string,
): Snippet[] => {
	let categoryFiltered = snippets;

	// Apply category filter
	if (categoryId === SNIPPET_CATEGORY_IDS.MY_TEMPLATES) {
		categoryFiltered = snippets.filter((snippet) => snippet.scope === SnippetScope.USER);
	} else if (categoryId === SNIPPET_CATEGORY_IDS.FAVORITES) {
		categoryFiltered = snippets.filter((snippet) => snippet?.isFavorite);
	}

	// Apply search filter
	const trimmedSearchQuery = searchQuery.trim().toLowerCase();
	if (!trimmedSearchQuery) {
		return categoryFiltered;
	}

	return categoryFiltered.filter((snippet) => {
		const nameMatch = snippet.name.toLowerCase().includes(trimmedSearchQuery);
		const ownerMatch = snippet.createdBy.displayName.toLowerCase().includes(trimmedSearchQuery);
		const descriptionMatch = snippet.description?.toLowerCase().includes(trimmedSearchQuery);
		return nameMatch || ownerMatch || descriptionMatch;
	});
};

/**
 * Pure function to sort snippets based on the selected sort order.
 * Returns a new sorted array without mutating the input.
 */
export const sortSnippets = (snippets: Snippet[], sortOrder: SnippetSortOrder): Snippet[] => {
	const sorted = [...snippets];

	switch (sortOrder) {
		case SnippetSortOrder.CREATED_AT:
			return sorted.sort(
				(a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
			);
		case SnippetSortOrder.LAST_UPDATED:
			return sorted.sort(
				(a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
			);
		case SnippetSortOrder.POPULARITY:
			// TODO: sort by popularity when field is available; falling back to createdAt for now
			return sorted.sort(
				(a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
			);
		case SnippetSortOrder.ALPHABETICAL:
			return sorted.sort((a, b) => a.name.localeCompare(b.name));
		default:
			return sorted;
	}
};

/**
 * Hook for managing snippet filter and sort state locally.
 *
 * This hook manages category, search query, and sort order filters independently
 * for each component, preventing filter state conflicts when multiple
 * pages/components use snippets.
 *
 * @param snippets - Array of snippets to filter and sort
 * @returns Object containing:
 *   - selectedCategoryId: Current category filter
 *   - searchQuery: Current search query
 *   - sortOrder: Current sort order
 *   - filteredSnippets: Snippets filtered and sorted by current settings
 *   - setCategoryFilter: Set the category filter
 *   - setSearchQuery: Set the search query
 *   - setSortOrder: Set the sort order
 *   - resetFilters: Reset filters to defaults
 */
export const useSnippetFilterAndSort = (snippets: Snippet[]) => {
	const [selectedCategoryId, setSelectedCategoryId] =
		useState<SNIPPET_CATEGORY_IDS>(DEFAULT_CATEGORY_ID);
	const [searchQuery, setSearchQuery] = useState('');
	const [sortOrder, setSortOrder] = useState<SnippetSortOrder>(DEFAULT_SNIPPET_SORT_ORDER);

	const filteredSnippets = sortSnippets(
		filterSnippets(snippets, selectedCategoryId, searchQuery),
		sortOrder,
	);

	return {
		selectedCategoryId,
		searchQuery,
		sortOrder,
		filteredSnippets,
		setCategoryFilter: setSelectedCategoryId,
		setSearchQuery,
		setSortOrder,
		resetFilters: () => {
			setSelectedCategoryId(DEFAULT_CATEGORY_ID);
			setSearchQuery('');
			setSortOrder(DEFAULT_SNIPPET_SORT_ORDER);
		},
	};
};
